/* Built In Imports */
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
  createRef,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

/* External Imports */
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Select,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import parser from 'html-react-parser';
import { nanoid } from 'nanoid';
import { useMediaQuery } from 'react-responsive';
import * as Yup from 'yup';
const ReCAPTCHA = dynamic(() => import('react-google-recaptcha'));

/* Internal Imports */
/* Components */
import config from '@config';
import CountriesList from '@constants/CountriesList';
const InputUI = dynamic(() => import('@lb/components/Footer/InputUI'));

/* Services */
import { addSubscription } from 'services/newsletterService';

/**
 * Renders the Subscribe Box component
 *
 * @param sectionContent
 * @returns {ReactElement} Subscribe Box component.
 */

const ArticleSubscribeBox = ({ sectionContent, lang }) => {
  const router = useRouter();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const finalRef = useRef(null);
  const [messageTemp, setMessageTemp] = useState('');
  const [loading, setLoading] = useState(false);
  const countries = CountriesList;
  const recaptchaRef = createRef();
  memo(countries);
  const isSmallScreen = useMediaQuery({ maxWidth: 350 });
  const initialVal = {
    name: '',
    email: '',
    country: '',
    'opt-in': '',
    newsletters: {},
    source: `${config.BASE_PATH}${router.asPath}`,
  };

  initialVal.newsletters[sectionContent?.newsletterIdentifier] =
    sectionContent?.newsletterIdentifier;

  const ValidationSchema = Yup.object().shape({
    name: Yup.string()
      .matches(/^([a-zA-Z ])+$/u, {
        message: 'Name is invalid',
      })
      .max(100, 'Name is invalid')
      .required('Name is required'),
    email: Yup.string()
      .required('Email is required')
      .matches(/^[a-zA-Z]+[a-zA-Z0-9._]+@[a-zA-Z]+(\.[a-zA-Z]{2,15})+$/, {
        message: 'Email is invalid',
      }),
    country: Yup.string().required('Country is required'),
    recaptcha: Yup.string().required('Cannot go past captcha'),
  });

  const onSubmit = useCallback(async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    let formValues = values;
    formValues['language'] = lang;
    setSubmitting(true);
    let formResponse = await addSubscription(formValues);
    setSubmitting(false);

    resetForm();

    if (typeof formResponse === 'string') {
      setMessageTemp(
        `<div >Sorry something went wrong. We are not able to process your request right now.</div>`
      );
    } else if (
      formResponse[0]?.status_code === 101 ||
      formResponse[0]?.status_code === 102 ||
      formResponse[0]?.status_code === 103 ||
      formResponse[0]?.status_code === 104 ||
      formResponse[0]?.status_code === 105 ||
      formResponse[0]?.status_code === 106 ||
      formResponse[0]?.status_code === 500
    ) {
      setMessageTemp(formResponse[0]?.message_template);
    } else {
      setMessageTemp(
        `<div >Sorry something went wrong. We are not able to process your request right now.</div>`
      );
    }
    // window.dataLayer = window.dataLayer || [];
    // window.dataLayer.push({
    //   event: 'msr_vp_pushed',
    //   virtual_path: '/shiva_followers_vp_success',
    // });
    onOpen();
    setLoading(false);
    const popupUrl =
      router.asPath.indexOf('?popup=1') > -1
        ? router.asPath
        : router.asPath.indexOf('?') > -1
        ? `${router.asPath}&popup=1`
        : `${router.asPath}?popup=1`;
    window.history.pushState(window.history.state, '', popupUrl);
    window['loc'] = popupUrl;
  });

  useEffect(() => {
    if (!isOpen && window?.loc?.indexOf('popup=1') > -1) {
      const parentUrl =
        location.origin +
        location.pathname +
        location.search.replace(/[\?&]popup=1/, '').replace(/^&/, '?');
      window.history.pushState(window.history.state, '', parentUrl);
    }
    // recaptchaRef?.current?.reset();
  }, [isOpen]);

  useEffect(() => {
    window.onpopstate = e => {
      // consoleLog('onpopstate', router, window, e);
      if (window?.loc?.indexOf('popup=1') > -1) {
        // consoleLog('onpopstate return', router.asPath);
        onClose();
        window['loc'] = router.asPath;
      }
    };
  }, [router.asPath]);
  return (
    <>
      <Box w="100%" maxW={1330} mx="auto" bg="#251C1C" py="60px">
        <Box maxW="447px" mx="auto">
          <Text
            mb="8px"
            color="#8E8E8E"
            textAlign="center"
            fontSize="26px"
            lineHeight="34px"
            fontFamily="FedraSansStd-Book"
            fontStyle="normal"
            fontWeight="400"
          >
            <Text as="span" color="#E19254">
              {sectionContent?.content?.json?.titleHighlight}{' '}
            </Text>

            {sectionContent?.content?.json?.title}
          </Text>
          <Text
            color="#8E8E8E"
            textAlign="center"
            fontSize={{ base: '12px', md: '14px' }}
            lineHeight="20px"
            fontFamily="FedraSansStd-Book"
            fontStyle="normal"
            fontWeight="400"
            mb={{ base: '22px', md: '20px' }}
          >
            {sectionContent?.content?.json?.description}
          </Text>
        </Box>

        <Flex
          display="flex"
          flexWrap="wrap"
          alignItems={{ base: 'center', md: 'center' }}
          mx={{ base: 'auto' }}
          justifyContent={{ base: 'center', md: 'center' }}
        >
          <Formik
            initialValues={initialVal}
            validationSchema={ValidationSchema}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {props => (
              <Form
                onSubmit={props.handleSubmit}
                style={{
                  display: 'grid',
                  rowGap: '21px',
                  columnGap: '10px',
                  boxSizing: 'border-box',
                }}
              >
                <Flex
                  flexDirection={{ base: 'column', md: 'row' }}
                  mx={{ base: 'auto' }}
                  alignItems="center"
                  justifyContent="center"
                >
                  {sectionContent?.content?.json?.name && (
                    <Field name="name">
                      {({ field, form }) => (
                        <FormControl
                          isRequired
                          isInvalid={form.errors.name && form.touched.name}
                          display="flex"
                          flexDir="column"
                          fontFamily="FedraSansStd-book"
                        >
                          <InputUI
                            fontFamily="'FedraSansStd-book', sans-serif"
                            color="#605D5D"
                            field={field}
                            id="UserName"
                            maxW={{
                              base: isSmallScreen ? '280px' : '311px',
                              md: '188px',
                            }}
                            height={{ base: '45px', md: '44px' }}
                            placeholder={
                              sectionContent?.content?.json?.name || 'Full Name'
                            }
                            onChange={props.handleChange}
                            padding="20px"
                            borderRadius="6px"
                            _placeholder={{
                              color: '#605D5D',
                              fontSize: '16px',
                            }}
                          />
                          <FormErrorMessage
                            justifyContent="left"
                            position="absolute"
                            margin="0"
                            bottom="-12px"
                            left="5px"
                          >
                            {form.errors.name}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  )}
                  {sectionContent?.content?.json?.email && (
                    <Field name="email">
                      {({ field, form }) => (
                        <FormControl
                          isRequired
                          isInvalid={form.errors.email && form.touched.email}
                        >
                          <InputUI
                            fontFamily="'FedraSansStd-book', sans-serif"
                            color="#605D5D"
                            // border="0.3px solid #000000"
                            field={field}
                            id="userEmail"
                            height={{ base: '45px', md: '44px' }}
                            maxW={{
                              base: isSmallScreen ? '280px' : '311px',
                              md: '188px',
                            }}
                            placeholder={sectionContent?.content?.json?.email}
                            borderRadius="6px"
                            onChange={props.handleChange}
                            padding="20px"
                            _placeholder={{
                              color: '#605D5D',
                              fontSize: '16px',
                            }}
                          />
                          <FormErrorMessage
                            justifyContent="left"
                            position="absolute"
                            margin="0"
                            bottom="-12px"
                            left="5px"
                          >
                            {form.errors.email}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  )}
                  {sectionContent?.content?.json?.country && (
                    <Field name="country">
                      {({ field, form }) => (
                        <FormControl
                          isRequired
                          isInvalid={
                            form.errors.country && form.touched.country
                          }
                          width="100%"
                          // m={{ base: '0 0 10px 0' }}
                        >
                          <Select
                            key="country"
                            {...field}
                            id="userCountry"
                            isRequired={true}
                            placeholder={
                              sectionContent?.content?.json?.country ||
                              'Country'
                            }
                            border="none"
                            width={{ base: '100%', md: '188px' }}
                            height={{ base: '45px', md: '44px' }}
                            maxW={{
                              md: '188px',
                              base: isSmallScreen ? '280px' : '311px',
                            }}
                            rounded="0"
                            margin="4.5px"
                            fontFamily="FedraSansStd-Book"
                            _focus={{}}
                            _hover={{}}
                            bg="#C8B9A6"
                            color="#957F68"
                          >
                            {CountriesList.map(country => {
                              return (
                                <option key={nanoid()} value={country.value}>
                                  {country.name}
                                </option>
                              );
                            })}
                          </Select>
                          <FormErrorMessage
                            justifyContent="left"
                            position="absolute"
                            margin="0"
                            bottom="-12px"
                            left="5px"
                          >
                            {form.errors.country}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  )}
                </Flex>

                <Grid
                  flexDirection={{ base: 'column', md: 'row' }}
                  // alignItems="center"
                  maxW={{ base: 'auto', md: '386px' }}
                  mx="auto"
                  maxH={{ base: 'auto', md: '45px' }}
                  placeItems="center"
                  gridAutoFlow={{ base: 'row', md: 'column' }}
                  alignContent="center"
                  justifyContent="center"
                >
                  <Field name="recaptcha">
                    {({ field, form }) => (
                      <FormControl
                        isInvalid={
                          form.errors.recaptcha && form.touched.recaptcha
                        }
                        //   width={{ base: 'auto', xl: 'calc((100% - 40px)/3)' }}
                        m={{ base: '0', md: '0 0px' }}
                        textAlign="center"
                        display="flex"
                        maxW={{ base: '100%', md: '201px' }}
                        height="50px"
                        maxH={20}
                        justifyContent="center"
                      >
                        <Box
                          mx={{ base: 'auto', md: '0' }}
                          mt={{ base: '0', md: '0', lg: '4px' }}
                          transformOrigin={{
                            base: '172px -50px',
                            lg: '10px -30px',
                          }}
                          transform={{ base: 'scale(0.8)', lg: 'scale(0.8)' }}
                        >
                          {config.RECAPTCHA_KEY && (
                            <ReCAPTCHA
                              ref={recaptchaRef}
                              sitekey={config.RECAPTCHA_KEY}
                              onChange={success => {
                                success === null
                                  ? recaptchaRef.current.reset()
                                  : props.setFieldValue('recaptcha', success);
                              }}
                              onErrored={error => {
                                props.setFieldValue('recaptcha', '');
                                recaptchaRef.current.reset();
                              }}
                              size="normal"
                            />
                          )}
                        </Box>
                        <FormErrorMessage
                          justifyContent="left"
                          position="absolute"
                          margin="0"
                          bottom="-12px"
                          left="5px"
                        >
                          {form.errors.recaptcha}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>

                  <Box
                    display="grid"
                    placeContent="center"
                    mt={{ base: '30px', md: '0' }}
                  >
                    <Button
                      type="submit"
                      variant="secondary"
                      w="177px"
                      h="46px"
                      margin="10px 4.5px"
                      fontWeight={500}
                      disabled={
                        !props.isValid || !props.dirty || loading ? true : false
                      }
                    >
                      <Flex justifyContent="center" alignItems="center">
                        <Text pr={'5px'}>
                          {loading
                            ? 'Submitting..'
                            : sectionContent?.content?.json?.submitButton}
                        </Text>
                      </Flex>
                    </Button>
                  </Box>
                </Grid>
              </Form>
            )}
          </Formik>
        </Flex>
      </Box>

      {messageTemp && (
        <Box
          maxW={messageTemp ? { base: '90%', md: '476px' } : 'auto'}
          m="20px auto"
        >
          <Modal
            finalFocusRef={finalRef}
            isOpen={isOpen}
            onClose={onClose}
            isCentered
            motionPreset="slideInBottom"
            size={!messageTemp ? 'xl' : '4xl'}
            blockScrollOnMount={true}
            closeOnOverlayClick={false}
            // scrollBehavior="inside"
          >
            <ModalOverlay />
            <ModalContent p="30px">
              <ModalCloseButton color="#9F9F9F" mt="1" />
              <ModalBody>{parser(messageTemp || '')}</ModalBody>
            </ModalContent>
          </Modal>
        </Box>
      )}
    </>
  );
};

export default ArticleSubscribeBox;
